import { useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { minutosDeAgora } from '@/lib/fuso';
import { ReguaDia } from '@/ui/components/regua-dia';
import { Segmentado } from '@/ui/components/segmentado';
import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Checkbox } from '@/ui/primitives/checkbox';
import { Interruptor } from '@/ui/primitives/interruptor';
import {
  type ConfiguracaoCapturaResposta,
  type EmpresaDetalhe,
  horariosDoDia,
  INTERVALO_MINIMO_CAPTURA_MINUTOS,
  type TipoAgendaCaptura,
} from '@fiscoai/contracts';

import { useSalvarConfiguracaoCaptura } from './api';
import { AvisoCertificado } from './aviso-certificado';
import { CampoHora, CampoIntervalo, HorariosFixos, SeletorDias } from './campos-agenda';
import { descreverAgenda } from './descrever-agenda';
import { ProximasConsultas } from './proximas';
import {
  deConfiguracao,
  errosDoRascunho,
  paraConfiguracao,
  type RascunhoCaptura,
} from './rascunho';
import { motivoSemCertificado } from './textos';

const MODOS = [
  { valor: 'HORARIOS', rotulo: 'Horários fixos', descricao: 'Ex.: 08:00, 12:00 e 16:45' },
  { valor: 'JANELA', rotulo: 'Ao longo do expediente', descricao: 'De tempos em tempos' },
  { valor: 'CONTINUA', rotulo: 'O dia todo', descricao: '24 h, todos os dias' },
] as const satisfies readonly { valor: TipoAgendaCaptura; rotulo: string; descricao: string }[];

const ESPERA = `A SEFAZ pede 1 h entre consultas sem novidade e bloqueia o CNPJ se consultar antes: o FiscoAI deixa ${String(INTERVALO_MINIMO_CAPTURA_MINUTOS)} min.`;

function CamposDoModo({
  r,
  mudar,
  erros,
}: {
  r: RascunhoCaptura;
  mudar: (parcial: Partial<RascunhoCaptura>) => void;
  erros: ReturnType<typeof errosDoRascunho>;
}) {
  if (r.tipo === 'CONTINUA')
    return (
      <CampoIntervalo
        valor={r.intervaloContinuo}
        aoMudar={(intervaloContinuo) => {
          mudar({ intervaloContinuo });
        }}
        erro={erros.intervaloMinutos}
      />
    );
  const dias = (
    <SeletorDias
      dias={r.dias}
      aoMudar={(d) => {
        mudar({ dias: d });
      }}
      erro={erros.dias}
    />
  );
  if (r.tipo === 'HORARIOS')
    return (
      <>
        <HorariosFixos
          horarios={r.horarios}
          aoMudar={(horarios) => {
            mudar({ horarios });
          }}
          erro={erros.horarios}
        />
        {dias}
      </>
    );
  return (
    <>
      <div className="flex flex-wrap gap-3">
        <CampoHora
          rotulo="Das"
          valor={r.inicio}
          aoMudar={(inicio) => {
            mudar({ inicio });
          }}
          erro={erros.inicio}
        />
        <CampoHora
          rotulo="Até"
          valor={r.fim}
          aoMudar={(fim) => {
            mudar({ fim });
          }}
          erro={erros.fim}
        />
        <CampoIntervalo
          valor={r.intervaloJanela}
          aoMudar={(intervaloJanela) => {
            mudar({ intervaloJanela });
          }}
          erro={erros.intervaloMinutos}
        />
      </div>
      {dias}
    </>
  );
}

function descricaoInterruptor(ativa: boolean, semCertificado: boolean): string {
  if (ativa) return 'Ligada: consulta a SEFAZ nos horários abaixo.';
  if (semCertificado) return 'Liga depois do envio do certificado A1.';
  return 'Desligada: nenhuma consulta automática é feita.';
}

function avisoProximas(alterado: boolean, ativa: boolean): string | null {
  if (alterado) return 'Salve para ver as próximas consultas desta agenda.';
  return ativa ? null : 'Ligue a captura para agendar as consultas.';
}

function SecaoLigar({
  empresa,
  podeEnviar,
  r,
  mudar,
}: {
  empresa: EmpresaDetalhe;
  podeEnviar: boolean;
  r: RascunhoCaptura;
  mudar: (parcial: Partial<RascunhoCaptura>) => void;
}) {
  const semCertificado = motivoSemCertificado(empresa);
  return (
    <SecaoFormulario
      titulo="Captura automática"
      descricao="O FiscoAI busca na SEFAZ as notas emitidas contra este CNPJ, nos horários que você escolher. Cada consulta traz só o que é novo."
    >
      {semCertificado && (
        <AvisoCertificado empresa={empresa} motivo={semCertificado} podeEnviar={podeEnviar} />
      )}
      <Interruptor
        rotulo="Captura automática"
        estado={r.ativa ? 'Ligada' : 'Desligada'}
        ligado={r.ativa}
        disabled={semCertificado !== null && !r.ativa}
        aoMudar={(ativa) => {
          mudar({ ativa });
        }}
        descricao={descricaoInterruptor(r.ativa, semCertificado !== null)}
      />
      <Checkbox
        marcado={r.cienciaAutomatica}
        aoMudar={(cienciaAutomatica) => {
          mudar({ cienciaAutomatica });
        }}
        rotulo="Dar ciência da operação automaticamente"
        descricao="A SEFAZ só libera o XML completo depois da ciência (até 10 dias da emissão). Ela pede a confirmação da operação em até 90 dias."
      />
    </SecaoFormulario>
  );
}

/** Edição da captura (quem tem `empresa.captura.gerenciar`). Tudo vale só ao salvar. */
export function FormularioCaptura({
  empresa,
  salva,
  podeEnviarCertificado,
}: {
  empresa: EmpresaDetalhe;
  podeEnviarCertificado: boolean;
  salva: ConfiguracaoCapturaResposta;
}) {
  const [r, setR] = useState(() => deConfiguracao(salva));
  const salvar = useSalvarConfiguracaoCaptura(empresa.id);
  const mudar = (parcial: Partial<RascunhoCaptura>) => {
    setR((atual) => ({ ...atual, ...parcial }));
  };
  const erros = errosDoRascunho(r);
  const valido = Object.keys(erros).length === 0;
  const configuracao = paraConfiguracao(r);
  const alterado =
    JSON.stringify(configuracao) !== JSON.stringify(paraConfiguracao(deConfiguracao(salva)));
  const enviar = () => {
    if (!valido || !alterado) return;
    salvar.mutate(configuracao, {
      onSuccess: () => toast.success('Captura salva'),
      onError: (erro) => toast.error(mensagemDeErro(erro)),
    });
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        enviar();
      }}
    >
      <SecaoLigar empresa={empresa} podeEnviar={podeEnviarCertificado} r={r} mudar={mudar} />
      <SecaoFormulario
        titulo="Quando buscar"
        descricao={ESPERA}
        acoes={
          <>
            {alterado && (
              <Button
                variante="fantasma"
                onClick={() => {
                  setR(deConfiguracao(salva));
                }}
              >
                Descartar alterações
              </Button>
            )}
            <Button
              type="submit"
              variante="primario"
              disabled={!valido || !alterado}
              carregando={salvar.isPending}
            >
              Salvar captura
            </Button>
          </>
        }
      >
        <Segmentado
          rotulo="Quando buscar"
          opcoes={MODOS}
          valor={r.tipo}
          aoMudar={(tipo) => {
            mudar({ tipo });
          }}
        />
        <CamposDoModo r={r} mudar={mudar} erros={erros} />
        <ReguaDia
          horarios={valido ? horariosDoDia(configuracao.agenda) : []}
          esperaMinutos={INTERVALO_MINIMO_CAPTURA_MINUTOS}
          agoraMinutos={minutosDeAgora(salva.fuso)}
          legenda={
            valido ? descreverAgenda(configuracao.agenda) : 'Corrija a agenda para ver a régua.'
          }
        />
        <ProximasConsultas
          proximas={salva.proximasExecucoes}
          fuso={salva.fuso}
          aviso={avisoProximas(alterado, salva.ativa)}
        />
      </SecaoFormulario>
    </form>
  );
}
